import { DOCUMENT, Location, registerLocaleData } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { BehaviorSubject, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

import { TranslocoService } from '@jsverse/transloco';
import dayjs from 'dayjs';
import { ar_EG, cs_CZ, de_DE, en_GB, en_US, es_ES, hu_HU, NzI18nInterface, pl_PL, pt_PT, sk_SK, sl_SI } from 'ng-zorro-antd/i18n';
import { CookieService } from 'ngx-cookie-service';

import { ROUTES_PREFIX as AUTH_ROUTES_PREFIX } from '@client/app/auth/auth.routes';

import { LanguageLocaleService } from './language-locale.service';

import { AngularLocaleService } from '../../../../client/src/app/shared/services/angular-locale.service';
import { environment } from '../../../../client/src/environments/environment';

export interface LocalizationConfig {
  locoLang: string;
  fileName: string;
  project: 'client' | 'admin';
}

export type LanguageKey = (typeof ESG_SUBPAGES_FULLY_TRANSLATED_LANGUAGES)[number];

export const COOKIE_LANGUAGE_KEY = 'lang';
const DEFAULT_LANG = 'en';

/**
 * Public profile of companies, their activities, the newsfeed and 404 page
 */
const USER_SCOPE = [
  'newsfeed',
  '404',
  'profile',
  'activity'
];

const USER_SCOPE_LANGUAGES = [
  'gb',
  'en',
  'cs',
  'sk',
  'de',
  'ar'
];

/**
 * Other client facing pages, such as media/blog
 * Previously all static business pages - moved to solidpixels
 */
const CLIENT_LANGUAGES = [
  'gb',
  'en',
  'cs',
  'sk',
  'de'
];

/**
 * Pages for ESG clients - admin dashboard, esg and requests/tasks
 */
const ESG_SUBPAGES_FULLY_TRANSLATED = [
  'task-management',
  'admin-dashboard',
  'esg-management'
];

const ESG_SUBPAGES_FULLY_TRANSLATED_LANGUAGES: LanguageCode[] = [
  'gb',
  'en',
  'cs',
  'sk',
  'sl',
  'pl',
  'hu',
  'de'
  // 'pt' // preparation for next portuguese translation
] as const;

const ADMIN_PAGES_FULLY_TRANSLATED = [
  'admin',
  'activity-admin',
  'data-analytics',
  'analytics',
  'report',
  'newsletter',
  'commitments-management',
  'certification',
  'knowledge-base',
  'profile-ideas'
];

const ADMIN_CREATE_PAGES_FULLY_TRANSLATED = [
  'activity',
  'profile'
];

const ADMIN_PAGES_FULLY_TRANSLATED_LANGUAGES: LanguageCode[] = [
  'gb',
  'en',
  'cs',
  'sk'
];

// TAKEN FROM i18n-download.mjs
const LOCALIZATIONS: LocalizationConfig[] = [
  { locoLang: 'ar', fileName: 'ar', project: 'client' },
  { locoLang: 'cs-CZ', fileName: 'cs', project: 'client' },
  { locoLang: 'cs-CZ', fileName: 'cs', project: 'admin' },
  { locoLang: 'de-DE', fileName: 'de', project: 'client' },
  { locoLang: 'en-GB', fileName: 'de', project: 'admin' },
  { locoLang: 'en-GB', fileName: 'en', project: 'client' },
  { locoLang: 'en-GB', fileName: 'en', project: 'admin' },
  { locoLang: 'en-GB', fileName: 'es', project: 'client' },
  { locoLang: 'en-GB', fileName: 'es', project: 'admin' },
  { locoLang: 'en-GB', fileName: 'gb', project: 'client' },
  { locoLang: 'en-GB', fileName: 'gb', project: 'admin' }, // en should be enough, as it is the default language, just to be sure
  { locoLang: 'hu-HU', fileName: 'hu', project: 'client' },
  { locoLang: 'hu-HU', fileName: 'hu', project: 'admin' },
  { locoLang: 'pl-PL', fileName: 'pl', project: 'client' },
  { locoLang: 'pl-PL', fileName: 'pl', project: 'admin' },
  { locoLang: 'en-GB', fileName: 'pt', project: 'client' }, // pt-PT
  { locoLang: 'en-GB', fileName: 'pt', project: 'admin' }, // pt-PT
  { locoLang: 'sl-SI', fileName: 'sl', project: 'client' },
  { locoLang: 'sl-SI', fileName: 'sl', project: 'admin' },
  { locoLang: 'sk-SK', fileName: 'sk', project: 'client' },
  { locoLang: 'sk-SK', fileName: 'sk', project: 'admin' }
];

const ADMIN_LOCALIZATIONS = Object.fromEntries(
  LOCALIZATIONS.filter(localization => localization.project === 'admin').map(localization => {
    return [
      localization.fileName,
      localization.locoLang
    ];
  })
);

/**
 * Creates a map (object) of admin localizations { 'shortCode': 'fullLocale' }
 * containing ONLY the languages marked as fully translated.
 *
 * It works by:
 * 1. Taking all `ADMIN_LOCALIZATIONS`.
 * 2. Filtering them, keeping only entries where the language key (like 'en', 'cs') is present
 * in the `ESG_SUBPAGES_FULLY_TRANSLATED_LANGUAGES` array.
 *
 * Why `key as (typeof ...)[number]`? `Object.entries` provides the `key` as a general `string`.
 * `ESG_SUBPAGES_FULLY_TRANSLATED_LANGUAGES` uses `as const`, so TypeScript knows it contains
 * only specific values like 'gb', 'en', 'cs', etc. (its type is specific, like `'gb' | 'en' | ...`).
 * This `key as ...` is a type assertion. We are telling TypeScript: "Trust us, this `key`
 * (which you think is just any `string`) is actually one of those specific language codes
 * from the `ESG_SUBPAGES_FULLY_TRANSLATED_LANGUAGES` array." This satisfies TypeScript's
 * type checking for the `.includes()` method, which might expect a more specific type here.
 *
 * 3. Converting the filtered key-value pairs back into an object.
 *
 * The result is a subset of `ADMIN_LOCALIZATIONS` containing only the fully translated ones.
 * The type `: Partial<Record<LanguageKey, string>>` indicates this resulting object structure.
 */
export const ADMIN_FULLY_TRANSLATED_LOCALIZATIONS: Partial<Record<LanguageKey, string>> = Object.fromEntries(
  Object.entries(ADMIN_LOCALIZATIONS).filter(([key]) =>
    ESG_SUBPAGES_FULLY_TRANSLATED_LANGUAGES.includes(key as (typeof ESG_SUBPAGES_FULLY_TRANSLATED_LANGUAGES)[number])
  )
);

export type LanguageCode = keyof LanguageService['languageList'];

export interface LanguageDefinition {
  shCode: string;
  localeCode: string;
  localeZorro: NzI18nInterface;
  localeImports: () => Promise<any>[];
}

@Injectable()
export class LanguageService {
  current$ = new BehaviorSubject<LanguageCode>(this.getLanguageFromCookie() ?? DEFAULT_LANG);
  languageChangedByUser$ = new Subject<LanguageCode>();
  languageList = {
    // en
    en: {
      shCode: 'en',
      localeCode: 'en',
      localeZorro: en_US,
      localeImports: this.localeService.localeImports_en
    } as LanguageDefinition,
    gb: {
      shCode: 'gb',
      localeCode: 'en-GB',
      localeZorro: en_GB,
      localeImports: this.localeService.localeImports_en_GB
    } as LanguageDefinition,
    de: {
      shCode: 'de',
      localeCode: 'de',
      localeZorro: de_DE,
      localeImports: this.localeService.localeImports_de
    } as LanguageDefinition,
    sk: {
      shCode: 'sk',
      localeCode: 'sk',
      localeZorro: sk_SK,
      localeImports: this.localeService.localeImports_sk
    } as LanguageDefinition,
    cs: {
      shCode: 'cs',
      localeCode: 'cs',
      localeZorro: cs_CZ,
      localeImports: this.localeService.localeImports_cs
    } as LanguageDefinition,
    pl: {
      shCode: 'pl',
      localeCode: 'pl',
      localeZorro: pl_PL,
      localeImports: this.localeService.localeImports_pl
    } as LanguageDefinition,
    es: {
      shCode: 'es',
      localeCode: 'es',
      localeZorro: es_ES,
      localeImports: this.localeService.localeImports_es
    } as LanguageDefinition,
    sl: {
      shCode: 'sl',
      localeCode: 'sl',
      localeZorro: sl_SI,
      localeImports: this.localeService.localeImports_sl
    } as LanguageDefinition,
    ar: {
      shCode: 'ar',
      localeCode: 'ar',
      localeZorro: ar_EG,
      localeImports: this.localeService.localeImports_ar
    } as LanguageDefinition,
    pt: {
      shCode: 'pt',
      localeCode: 'pt',
      localeZorro: pt_PT,
      localeImports: this.localeService.localeImports_pt
    } as LanguageDefinition,
    hu: {
      shCode: 'hu',
      localeCode: 'hu',
      localeZorro: hu_HU,
      localeImports: this.localeService.localeImports_hu
    } as LanguageDefinition
  };

  languageKeys = Object.keys(this.languageList) as LanguageCode[];

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private angularLocaleService: AngularLocaleService,
    private cookieService: CookieService,
    private localeService: LanguageLocaleService,
    private router: Router,
    private translocoService: TranslocoService,
    private location: Location
  ) {
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      const languageToSet = this.getLanguageForRoute(this.router.url);
      const urlLanguage = this.getLanguageFromLocationPath();

      if (languageToSet && this.translocoService.getActiveLang() !== languageToSet) {
        this.useLanguage(languageToSet, false);
      } else if (!urlLanguage) {
        // language not yet initialized in url from language, probably after redirect from auth service
        this.initByCookieOrBrowserLanguage(this.current$.value);
      }
    });
  }

  private _isAuthRoute(normalizedPath: string): boolean {
    return normalizedPath.startsWith(`/${AUTH_ROUTES_PREFIX}`);
  }

  isEsgPageFullyTranslated(_page: string, _identifier: string, subPage: string) {
    if (ESG_SUBPAGES_FULLY_TRANSLATED.includes(subPage)) {
      return true;
    }

    return false;
  }

  isAdminPageFullyTranslated(page: string, identifier: string, subPage: string) {
    if (ADMIN_CREATE_PAGES_FULLY_TRANSLATED.includes(page) && identifier === 'create') {
      return true;
    }

    if (ADMIN_PAGES_FULLY_TRANSLATED.includes(subPage)) {
      return true;
    }

    return false;
  }

  isUserScopePage(page: string) {
    if (USER_SCOPE.includes(page)) {
      return true;
    }

    return false;
  }

  /**
   * After user is loaded from backend the retrieved language can be different
   * from the one previously set in cookie or currently used - set correctly
   *
   * @param userLanguage
   */
  setLanguageAfterUserLoad(userLanguage: LanguageCode) {
    // we need to explicitly check for both cookie and active lang, because they can be different
    if (userLanguage && this.getLanguageFromCookie() !== userLanguage) {
      this.setLanguageToCookie(userLanguage);
    }

    if (userLanguage && this.current$.value !== userLanguage) {
      this.changeLanguage(userLanguage);
    }
  }

  #getPathWithoutLang(url: string, language?: LanguageCode | null): string {
    if (!language) {
      return url;
    }

    const normalizedPath = !url || url === '/' ? '' : url;
    const regExp = new RegExp(`^\/${language}`);
    return normalizedPath.replace(regExp, '');
  }

  /**
   * Some routes fallback to default language because of missing translation
   *
   * @param url
   * @param forcedLanguage - if forcedLanguage is not provided language is taken from url
   */
  getLanguageForRoute(url: string, forcedLanguage?: LanguageCode): LanguageCode | null {
    const urlLanguage = this.getLanguageFromLocationPath();
    const targetLanguage = forcedLanguage ?? urlLanguage ?? this.current$.value;

    // not much to do in case the language is not known from any source
    if (!targetLanguage) {
      return null;
    }

    const pathWithoutLang = this.#getPathWithoutLang(url, urlLanguage);

    const [
      page,
      identifier,
      _identifierId,
      subpage
    ] = this.getSegmentsFromUrl(pathWithoutLang);

    // initialization - do not override language
    if (!page && !subpage && !identifier) {
      return null;
    }

    // if page is admin page and language is not in supported languages for admin, return default language
    if (this.isEsgPageFullyTranslated(page, identifier, subpage)) {
      if (!ESG_SUBPAGES_FULLY_TRANSLATED_LANGUAGES.includes(targetLanguage)) {
        return DEFAULT_LANG;
      }
      return targetLanguage;
    }

    // if page is admin page and language is not in supported languages for admin, return default language
    if (this.isAdminPageFullyTranslated(page, identifier, subpage)) {
      if (!ADMIN_PAGES_FULLY_TRANSLATED_LANGUAGES.includes(targetLanguage)) {
        return DEFAULT_LANG;
      }
      return targetLanguage;
    }

    // if page is in user scope (limited list of pages translated to supported languages) page and
    // language is not in client languages, return default language
    if (this.isUserScopePage(page)) {
      if (!USER_SCOPE_LANGUAGES.includes(targetLanguage)) {
        return DEFAULT_LANG;
      }
      return targetLanguage;
    }

    if (!CLIENT_LANGUAGES.includes(targetLanguage)) {
      return DEFAULT_LANG;
    }

    // in this case the language should be hopefully translated
    return targetLanguage;
  }

  getSegmentsFromUrl(url: string) {
    // remove initial slash if exists
    if (url?.startsWith('/')) {
      url = url.substring(1) ?? '';
    }
    if (url?.includes('#')) {
      url = url.split('#')[0] ?? '';
    }
    if (url?.includes('?')) {
      url = url.split('?')[0] ?? '';
    }

    return url?.split('/');
  }

  initByLocationPath(locationPathLanguage: LanguageCode) {
    return this.useLanguage(locationPathLanguage).then(() => {});
  }

  initByCookieOrBrowserLanguage(languageToSet: LanguageCode): Promise<void> {
    const { hash, pathname, searchParams } = new URL(this.document.location.toString());
    const pathLanguage = this.getLanguageFromLocationPath();

    const normalizedPath = !pathname || pathname === '/' ? '' : pathname;

    const pathWithoutLang = this.#getPathWithoutLang(pathname, pathLanguage);
    const urlPath = `/${languageToSet}${pathWithoutLang}`;

    return this.useLanguage(languageToSet).then(() => {
      return this._isAuthRoute(normalizedPath)
        ? new Promise(resolve => resolve())
        : this.router
            .navigate([urlPath], {
              fragment: hash.replace('#', ''),
              preserveFragment: true,
              queryParams: Object.fromEntries(searchParams),
              queryParamsHandling: 'merge',
              replaceUrl: true
            })
            .then(() => {});
    });
  }

  initialize(): Promise<void> {
    const locationPathLanguage = this.getLanguageFromLocationPath();

    const cookieLanguage = this.getLanguageFromCookie();

    if (cookieLanguage) {
      return this.initByCookieOrBrowserLanguage(cookieLanguage);
    }

    if (locationPathLanguage) {
      this.setLanguageToCookie(locationPathLanguage);
      return this.initByLocationPath(locationPathLanguage);
    }

    const browserLanguage = this.getBrowserLanguage();

    if (browserLanguage) {
      this.setLanguageToCookie(browserLanguage);
      return this.initByCookieOrBrowserLanguage(browserLanguage);
    }

    return new Promise(resolve => resolve());
  }

  changeLanguage(languageCode: LanguageCode, navigateAway = true) {
    this.setLanguageToCookie(languageCode);

    return this.useLanguage(languageCode).then(() => {
      this.languageChangedByUser$.next(languageCode);

      const pathLanguage = this.getLanguageFromLocationPath();
      const langReplacedUrl = this.router.url.replace(`/${pathLanguage}/`, `/${languageCode}/`);

      if (!navigateAway) {
        // keep state in the history and do not replace by {} in order to keep the back working
        window.history.replaceState(this.location.getState(), '', langReplacedUrl);
        return new Promise(resolve => resolve(true));
      }

      return this.router.navigateByUrl(langReplacedUrl);
    });
  }

  useLanguage(languageCode: LanguageCode, changeCurrentLang = true): Promise<void> {
    const languageForRoute = this.getLanguageForRoute(this.router.url, languageCode) ?? languageCode;
    const locale = this.languageList[languageForRoute];

    if (!locale?.localeImports) {
      return new Promise(resolve => resolve());
    }

    return Promise.all(locale.localeImports()).then(modules => {
      registerLocaleData(modules[0].default, locale.localeCode, modules[1].default);
      this.angularLocaleService.setLocale(locale.shCode);
      this.translocoService.setDefaultLang(locale.shCode);
      this.translocoService.setActiveLang(locale.shCode);
      if (changeCurrentLang) {
        this.current$.next(languageCode); // we pass original language to current and not override
      }
      dayjs.locale(locale.localeCode.toLowerCase());
    });
  }

  getLanguageFromLocationPath(): LanguageCode | null {
    return (this.document.location.pathname?.match(`^\/(${this.languageKeys.join('|')})`)?.[1] as LanguageCode) ?? null;
  }

  convertBrowserLanguage(browserLanguage: string): LanguageCode {
    const fallbackLanguage = this.languageKeys.includes(browserLanguage as any) ? (browserLanguage as LanguageCode) : (DEFAULT_LANG as LanguageCode);

    // this should never happen, but yet it did happen
    if (typeof browserLanguage !== 'string') {
      return fallbackLanguage;
    }

    if (browserLanguage.startsWith('en')) {
      if (browserLanguage === 'en-GB') {
        return 'gb';
      }
      return 'en';
    }
    // iso XXX
    if (browserLanguage.match(/^[a-z]{2}-[A-Z]{2}/)) {
      const browserLangSplit = browserLanguage.slice(0, 2);
      return this.languageKeys.includes(browserLangSplit as any) ? (browserLangSplit as LanguageCode) : (DEFAULT_LANG as LanguageCode);
    }

    return fallbackLanguage;
  }

  getBrowserLanguage(): LanguageCode | null {
    return navigator.language ? this.convertBrowserLanguage(navigator.language) : null;
  }

  setLanguageToCookie(languageCode: LanguageCode) {
    this.cookieService.set(COOKIE_LANGUAGE_KEY, languageCode, undefined, '/', undefined, environment.cookies_secure);
  }

  getLanguageFromCookie() {
    return this.cookieService.get(COOKIE_LANGUAGE_KEY) as LanguageCode;
  }
}
